import React, { useEffect, useRef } from 'react';
import { useUserRegion } from '../../hooks/useUserRegion';
import { CTAButton } from '../ui/CTAButton';
import { FadeIn } from '../ui/FadeIn';
const INCLUDED_ITEMS = [{
  name: '7-Day HD Video Program (Fundamentals → Protocols → Project)',
  value: '$197 value'
}, {
  name: 'Anti-Bubble Protocol™',
  value: 'Included'
}, {
  name: 'Anti-Sticky Resin Protocol™',
  value: 'Included'
}, {
  name: 'Pre-Pour Checklist (printable, reusable)',
  value: 'Included'
}, {
  name: 'Real-Time Error Correction Guide',
  value: '$47 value'
}, {
  name: 'Guided Sell-Ready Project',
  value: '$97 value'
}, {
  name: 'Sell-Ready Roadmap (your first Etsy steps)',
  value: 'Included'
}, {
  name: 'Safety & Workspace Setup Module',
  value: 'Included'
}, {
  name: 'Lifetime Access on Any Device',
  value: 'Included'
}, {
  name: 'Private Community + Support',
  value: 'Included'
}];
export function ProductPresentation() {
  const {
    formattedPrice,
    formattedRegularPrice,
    loading
  } = useUserRegion();
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      });
    }, {
      threshold: 0.2
    });
    if (videoRef.current) {
      observer.observe(videoRef.current);
    }
    return () => {
      if (videoRef.current) {
        observer.unobserve(videoRef.current);
      }
    };
  }, []);
  return <section className="w-full px-4 md:px-8 max-w-5xl mx-auto flex flex-col items-center" data-id="element-228">
      <FadeIn className="text-center mb-10" data-id="element-229">
        <span className="text-amber font-lato uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-230">
          The Complete System
        </span>
        <h2 className="font-playfair italic text-4xl md:text-5xl lg:text-6xl text-amber mb-6" data-id="element-231">
          Resin Without Errors™
        </h2>
        <p className="font-lato text-lg md:text-xl text-cream/80 max-w-2xl mx-auto" data-id="element-232">
          Everything you need to go from confused beginner to confident creator
          — in 7 structured days.
        </p>
      </FadeIn>

      {/* Product Image */}
      <FadeIn className="w-full max-w-4xl mx-auto mb-12" data-id="element-233">
        <img src="https://cdn.magicpatterns.com/uploads/fXUGbVPDoS2ikfP2mqz3G3/hf_20260220_035139_2d9eca2c-728c-4770-ba0d-7c75b42a186c.webp" alt="Resin Without Errors — 10-in-1 Premium Program with certificate of excellence, HD videos, step-by-step method, and sell-ready projects" className="w-full rounded-lg shadow-2xl shadow-amber/10" data-id="element-234" />
      </FadeIn>

      {/* What's Included */}
      <FadeIn direction="up" className="w-full max-w-3xl mx-auto mb-16" data-id="element-235">
        <h3 className="font-playfair text-2xl text-cream mb-6" data-id="element-236">
          What's Included:
        </h3>
        <ul className="flex flex-col gap-4 font-lato" data-id="element-237">
          {INCLUDED_ITEMS.map((item, index) => <li key={index} className="flex items-start justify-between gap-4 border-b border-darkBorder pb-4 last:border-0" data-id="element-238">
              <div className="flex items-start gap-3" data-id="element-239">
                <span className="text-amber mt-1 flex-shrink-0" data-id="element-240">✓</span>
                <span className="text-cream/90" data-id="element-241">{item.name}</span>
              </div>
              <span className="text-amber/70 text-sm whitespace-nowrap font-bold" data-id="element-242">
                {item.value}
              </span>
            </li>)}
        </ul>
        <div className="mt-4 pt-6 border-t border-amber/30 flex flex-col gap-3" data-id="element-243">
          <div className="flex justify-between items-center" data-id="element-244">
            <span className="font-lato text-cream/70" data-id="element-245">Program Value:</span>
            <span className="font-playfair text-lg text-cream/70 line-through opacity-70" data-id="element-246">
              $341+
            </span>
          </div>
          <div className="flex justify-between items-center" data-id="element-247">
            <span className="font-lato text-cream/70" data-id="element-248">
              + 10 Exclusive Bonuses:
            </span>
            <span className="font-playfair text-lg text-cream/70 line-through opacity-70" data-id="element-249">
              $165+
            </span>
          </div>
          <div className="pt-3 border-t border-amber/20 flex justify-between items-center" data-id="element-250">
            <span className="font-playfair text-xl text-amber font-bold" data-id="element-251">
              Total Value:
            </span>
            <span className="font-playfair text-2xl md:text-3xl text-amber font-bold" data-id="element-252">
              $506+
            </span>
          </div>
        </div>
      </FadeIn>

      {/* 7-Day Course Grid */}
      <FadeIn delay={150} className="w-full max-w-4xl mx-auto mb-24" data-id="element-253">
        <h3 className="font-playfair text-2xl md:text-3xl text-amber text-center mb-8" data-id="element-254">
          Your 7-Day Plan
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-255">
          {[{
          day: 'Day 1',
          title: 'Resin Fundamentals',
          icon: '🧪'
        }, {
          day: 'Day 2',
          title: 'Anti-Bubble Protocol™',
          icon: '🫧'
        }, {
          day: 'Day 3',
          title: 'Color Mixing',
          icon: '🎨'
        }, {
          day: 'Day 4',
          title: 'Your First Piece',
          icon: '✨'
        }, {
          day: 'Day 5',
          title: 'Error Correction',
          icon: '🔧'
        }, {
          day: 'Day 6',
          title: 'Sell-Ready Photography',
          icon: '📸'
        }, {
          day: 'Day 7',
          title: 'Ready to Sell',
          icon: '🏷️'
        }].map((item, i) => <div key={i} className="flex items-center gap-4 rounded-lg p-3 md:p-4 border-l-[3px] border-amber" style={{
          backgroundColor: '#1e1a14'
        }} data-id="element-256">
              <span className="text-2xl flex-shrink-0" aria-hidden="true" data-id="element-257">
                {item.icon}
              </span>
              <div data-id="element-258">
                <span className="text-amber font-lato text-xs uppercase tracking-widest font-bold block" data-id="element-259">
                  {item.day}
                </span>
                <span className="text-cream font-lato text-sm md:text-base" data-id="element-260">
                  {item.title}
                </span>
              </div>
            </div>)}
        </div>
      </FadeIn>

      {/* Pricing Card */}
      <FadeIn delay={200} className="w-full max-w-2xl mx-auto" data-id="element-261">
        <div className="bg-darkCard border-2 border-amber rounded-2xl p-8 md:p-12 text-center relative glow-amber-lg" data-id="element-262">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber text-dark px-6 py-1 rounded-full font-lato font-bold text-sm uppercase tracking-wider whitespace-nowrap" data-id="element-263">
            Launch Offer
          </div>

          <div className="mb-8" data-id="element-264">
            <p className="text-cream/60 font-lato mb-2 uppercase tracking-wide text-sm" data-id="element-265">
              Regular Price:
            </p>
            <p className="text-2xl text-cream/40 line-through font-playfair mb-4" data-id="element-266">
              {loading ? <span className="w-16 h-8 bg-darkBorder rounded animate-pulse inline-block" data-id="element-267"></span> : formattedRegularPrice}
            </p>
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-268">
              <p className="text-6xl md:text-7xl font-playfair text-amber font-bold" data-id="element-269">
                {loading ? <span className="w-32 h-16 bg-amber/20 rounded animate-pulse inline-block" data-id="element-270"></span> : formattedPrice}
              </p>
              {!loading && <span className="text-white font-bold text-sm px-3 py-1 rounded-full" style={{
              backgroundColor: '#C0392B'
            }} data-id="element-271">
                  45% OFF
                </span>}
            </div>
            <p className="text-amber/80 font-lato text-sm italic mb-4" data-id="element-272">
              💰 Most students recover this investment with their very first
              sale.
            </p>
            <p className="text-cream/80 font-lato text-sm md:text-base" data-id="element-273">
              One-time payment. No subscriptions. No hidden fees.
            </p>
          </div>

          {/* Unified Urgency Block */}
          <div className="rounded-xl p-4 mb-6 border border-amber/30 text-center space-y-3" style={{
          backgroundColor: '#1e1a14'
        }} data-id="element-274">
            <div className="flex items-center justify-center gap-2" data-id="element-275">
              <span aria-hidden="true" data-id="element-276">🔥</span>
              <span className="text-cream/80 text-sm font-lato" data-id="element-277">
                Only <span className="text-amber font-bold" data-id="element-278">12</span> spots left
                at launch price
              </span>
            </div>
            <div className="w-full rounded-full h-2 overflow-hidden" style={{
            backgroundColor: '#333'
          }} data-id="element-279">
              <div className="h-full rounded-full transition-all duration-1000" style={{
              width: '60%',
              backgroundColor: '#C0392B'
            }} data-id="element-280"></div>
            </div>
            <p className="text-cream/40 text-xs font-lato" data-id="element-281">
              <span className="text-amber font-bold" data-id="element-282">8</span> of 20 spots
              remaining
            </p>
          </div>

          <div className="flex flex-col items-center gap-6" data-id="element-283">
            <CTAButton formattedPrice={formattedPrice} loading={loading} size="xl" className="w-full shadow-xl" data-id="element-284" />

            <div className="flex items-center justify-center gap-2 text-cream/50 text-sm font-lato" data-id="element-285">
              <span data-id="element-286">🔒</span> Secure checkout powered by Shopify
            </div>
          </div>
        </div>
      </FadeIn>

      {/* What Happens After You Click */}
      <FadeIn delay={220} className="w-full max-w-2xl mx-auto mt-16" data-id="element-287">
        <div className="text-center mb-10" data-id="element-288">
          <span className="text-amber font-lato uppercase tracking-widest text-sm font-bold mb-4 block" data-id="element-289">
            Here's What Happens After You Click
          </span>
          <h3 className="font-playfair text-3xl md:text-4xl lg:text-5xl text-cream" data-id="element-290">
            Get instant access in 3 simple steps
          </h3>
        </div>

        <div className="flex flex-col gap-6 mb-12" data-id="element-291">
          {[{
          icon: '🔒',
          step: 'Step 1',
          text: 'Complete your secure checkout in 60 seconds'
        }, {
          icon: '📧',
          step: 'Step 2',
          text: 'Check your email — your login arrives instantly'
        }, {
          icon: '▶️',
          step: 'Step 3',
          text: 'Start Day 1 right now — on any device, anywhere'
        }].map((item, i) => <div key={i} className="flex items-center gap-4 rounded-xl p-4 md:p-5 border border-darkBorder" style={{
          backgroundColor: '#1e1a14'
        }} data-id="element-292">
              <span className="text-2xl md:text-3xl flex-shrink-0" aria-hidden="true" data-id="element-293">
                {item.icon}
              </span>
              <div data-id="element-294">
                <span className="text-amber font-lato text-xs uppercase tracking-widest font-bold block mb-1" data-id="element-295">
                  {item.step}
                </span>
                <span className="text-cream font-lato text-sm md:text-base" data-id="element-296">
                  {item.text}
                </span>
              </div>
            </div>)}
        </div>

        <div className="rounded-[12px] overflow-hidden border border-darkBorder flex justify-center" style={{
        backgroundColor: '#1e1a14'
      }} data-id="element-297">
          <video ref={videoRef} src="https://cdn.magicpatterns.com/uploads/7ifZ1jgNrCkBe6Vrj8xNkr/Video%20Resina%20Whiout%20Errores%20(2)%20(1).mp4" loop muted playsInline preload="metadata" className="w-full h-auto max-h-[600px] object-contain rounded-[12px] pointer-events-none" data-id="element-298">
            Your browser does not support the video tag.
          </video>
        </div>
        <p className="text-center text-cream/40 text-sm font-lato italic mt-3" data-id="element-299">
          A quick look inside your members area
        </p>
      </FadeIn>

      {/* Payment Methods & Urgency */}
      <FadeIn delay={250} className="w-full max-w-2xl mx-auto mt-8 flex flex-col items-center gap-8" data-id="element-300">
        <div className="text-center w-full" data-id="element-301">
          <p className="text-cream/50 font-lato text-sm mb-4 uppercase tracking-wider" data-id="element-302">
            We accept all major payment methods:
          </p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 opacity-60 grayscale hover:grayscale-0 transition-all duration-300" data-id="element-303">
            {['Visa', 'Mastercard', 'Amex', 'PayPal', 'Apple Pay', 'Google Pay'].map(method => <div key={method} className="bg-cream/10 px-3 py-1.5 rounded text-xs font-lato font-bold tracking-wide" data-id="element-304">
                {method}
              </div>)}
          </div>
        </div>

        <div className="bg-darkCard border-l-4 border-amber p-6 rounded-r-lg w-full flex items-start gap-4" data-id="element-305">
          <span className="text-2xl flex-shrink-0" data-id="element-306">⚠️</span>
          <p className="font-lato text-cream/80 text-sm md:text-base leading-relaxed" data-id="element-307">
            <strong className="text-amber" data-id="element-308">Launch pricing ends soon.</strong>{' '}
            After this window, the price returns to{' '}
            {loading ? 'regular price' : formattedRegularPrice}. This page may
            be gone when you come back.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 bg-darkCard/50 border border-darkBorder p-6 md:p-8 rounded-xl w-full" data-id="element-309">
          <div className="w-24 h-24 rounded-full border-2 border-amber flex items-center justify-center flex-shrink-0 relative" data-id="element-310">
            <div className="absolute inset-1 border border-amber/30 rounded-full" data-id="element-311"></div>
            <div className="text-center leading-none" data-id="element-312">
              <span className="block text-2xl font-playfair text-amber font-bold" data-id="element-313">
                60
              </span>
              <span className="block text-[10px] font-lato uppercase tracking-widest text-cream/70 mt-1" data-id="element-314">
                Day
              </span>
            </div>
            <svg className="absolute inset-0 w-full h-full animate-[spin_20s_linear_infinite]" viewBox="0 0 100 100" data-id="element-315">
              <path id="curve" d="M 50 50 m -37 0 a 37 37 0 1 1 74 0 a 37 37 0 1 1 -74 0" fill="transparent" data-id="element-316" />
              <text className="text-[8px] font-lato uppercase tracking-widest fill-amber/80 font-bold" data-id="element-317">
                <textPath href="#curve" startOffset="0%" data-id="element-318">
                  MONEY BACK GUARANTEE • MONEY BACK GUARANTEE •
                </textPath>
              </text>
            </svg>
          </div>
          <div className="text-center md:text-left" data-id="element-319">
            <h4 className="font-playfair text-xl text-cream mb-2" data-id="element-320">
              60-Day Money Back Guarantee
            </h4>
            <p className="font-lato text-sm text-cream/70 leading-relaxed" data-id="element-321">
              Try the full 7-day system. If you're not making cleaner, better,
              sell-ready pieces — email us within 60 days for a full refund. No
              questions. No fine print.
            </p>
          </div>
        </div>
      </FadeIn>
    </section>;
}